import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useEsc } from 'hooks/useEsc';
import { useSelector } from 'hooks/useSelector';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doAseSetGroupFilter } from 'store/actions';
import {
  selectAseTplFieldGroup,
  selectAseTplFieldInstanceChecksForGroup,
  selectAseFieldGroupFilter,
  selectAseIsEditable,
} from 'store/assessments/selectors';
import { FieldRepeatableOrderEnum } from 'types/types';
import { SeparatorHorizontal } from 'components/SeparatorHorizontal';
import { OptionField } from 'components/Field/OptionField';
import { produce } from 'immer';
import { CardRow } from 'components/CardRow';
import { CardRowTitle } from 'components/CardRowTitle';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { getRepeatableFilterKey } from 'store/assessments/utilities';
import { ExplorerOptions } from '../Explorer/ExplorerOptions';

export const RepeatableOptionsModal: React.FC<
  ModalScreenProps<ModalScreens.AseFieldInstancesOptions>
> = ({ route }) => {
  const dispatch = useDispatch();
  const { groupId, listType } = route.params;

  const groupFilterKey = getRepeatableFilterKey(groupId, listType);
  const group = useSelector((state) => selectAseTplFieldGroup(state, groupId));
  const groupFilter = useSelector((state) =>
    selectAseFieldGroupFilter(state, groupFilterKey),
  );
  const checks = useSelector((state) =>
    selectAseTplFieldInstanceChecksForGroup(state, groupId),
  );
  const isEditable = useSelector(selectAseIsEditable);

  const handleSetOrder = (newOrderValue: FieldRepeatableOrderEnum) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          draft.order = newOrderValue;
        }),
      }),
    );

  const handleSetMissingValues = (newValue: boolean) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          draft.requiredFields = newValue;
        }),
      }),
    );

  const handleSetCheck = (
    checkId: string,
    value: 'required' | 'notRequired' | null,
  ) =>
    dispatch(
      doAseSetGroupFilter({
        groupId: groupFilterKey,
        data: produce(groupFilter, (draft) => {
          if (!value) {
            delete draft.checks[checkId];
          } else {
            draft.checks[checkId] = value;
          }
        }),
      }),
    );

  const handleClose = React.useCallback(
    () => dispatch(NAVIGATE_MODAL_BACK()),
    [dispatch],
  );

  React.useEffect(() => {
    if (!group) {
      handleClose();
    }
  }, [dispatch, group, handleClose]);

  useEsc(handleClose);

  if (!group) {
    return null;
  }

  return (
    <PageScrollView
      header={
        <NavigationHeader
          heading="View Options"
          subheading={group.name}
          onPressSaveButton={() => dispatch(NAVIGATE_MODAL_BACK())}
          saveButtonText="Done"
        />
      }
    >
      <Card label="Order By">
        <OptionField
          modalSize="small"
          modalLabel={`Order ${group.name} By`}
          value={[groupFilter.order]}
          onChange={(value) =>
            handleSetOrder(value[0] as FieldRepeatableOrderEnum)
          }
          options={[
            {
              name: 'Created (Default)',
              value: 'Oldest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.default,
            },
            {
              name: 'Created',
              value: 'Oldest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.createdAsc,
              groupName: 'Created',
            },
            {
              name: 'Created',
              value: 'Newest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.createdDesc,
              groupName: 'Created',
            },
            {
              name: 'Updated',
              value: 'Oldest first',
              labelAlignTop: true,
              internalValue: FieldRepeatableOrderEnum.updatedAsc,
              groupName: 'Updated',
            },
            {
              name: 'Updated',
              value: 'Newest first',
              internalValue: FieldRepeatableOrderEnum.updatedDesc,
              groupName: 'Updated',
              labelAlignTop: true,
            },
            ...Object.values(checks)
              .map((check) => [
                {
                  name: 'Check Last Completed',
                  value: `${check.name} • Oldest first`,
                  internalValue: `check-${check.id}-completed-asc`,
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Completed',
                  value: `${check.name} • Newest first`,
                  internalValue: `check-${check.id}-completed-desc`,
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Attempted',
                  value: `${check.name} • Oldest first`,
                  internalValue: `check-${check.id}-attempted-asc`,
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
                {
                  name: 'Check Last Attempted',
                  value: `${check.name} • Newest first`,
                  internalValue: `check-${check.id}-attempted-desc`,
                  groupName: `Check: ${check.name}`,
                  labelAlignTop: true,
                },
              ])
              .flat(),
          ]}
          expandedInlineFirst
          expandedInlineLast
        />
      </Card>

      {isEditable && <Card label={`Filter ${group.name}`} margin={false} />}

      {isEditable &&
        Object.values(checks).map((check) => (
          <Card key={`check-${check.id}`} marginBottomQuarter>
            <CardRowTitle label={check.name} />
            <ViewWithSeparators horizontal>
              <CardRow
                label="All"
                style={{ width: 100 }}
                radioValue={!groupFilter.checks[check.id]}
                onPress={() => handleSetCheck(check.id, null)}
              />
              <CardRow
                label="Outstanding"
                style={{ flex: 1 }}
                radioValue={groupFilter.checks[check.id] === 'required'}
                onPress={() => handleSetCheck(check.id, 'required')}
                showBadge={groupFilter.checks[check.id] === 'required'}
              />
              <CardRow
                label="Completed"
                style={{ flex: 1 }}
                radioValue={groupFilter.checks[check.id] === 'notRequired'}
                onPress={() => handleSetCheck(check.id, 'notRequired')}
                showBadge={groupFilter.checks[check.id] === 'notRequired'}
              />
            </ViewWithSeparators>
          </Card>
        ))}

      {isEditable && listType === 'added' && (
        <Card>
          <CardRow
            label="Incomplete Fields"
            value={`Only show ${group.name.toLowerCase()} where fields are incomplete`}
            labelAlignTop
            onPress={() => handleSetMissingValues(!groupFilter.requiredFields)}
            checkedValue={groupFilter.requiredFields}
            showBadge={groupFilter.requiredFields}
          />
        </Card>
      )}

      {listType === 'added' && (
        <>
          <SeparatorHorizontal withMarginBottom />
          <ExplorerOptions />
        </>
      )}
    </PageScrollView>
  );
};
